import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Camera, Image, MessageSquare, Users, Clock, Layers, Save, Check } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { InfoTooltip } from '../components/ExplanationLayers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * EDIT MY WORLD — Settings Page for Base Camp Content
 * 
 * Per FINAL DIRECTIVE:
 * - Actual edit screen (not a loop back to Base Camp)
 * - All Base Camp sections editable here
 * - NO recursive navigation
 */
export default function EditMyWorld() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    username: '',
    bio: '',
    profile_image: ''
  });
  const [visibility, setVisibility] = useState({
    feed: true,
    commitments: true,
    groups: true,
    commons: true,
    scuta: true,
    photos: true,
    expression: true,
    contacts: true,
    circles: true
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [profileRes, visibilityRes] = await Promise.all([
        axios.get(`${API}/api/user/profile`, { headers }),
        axios.get(`${API}/api/user/section-visibility`, { headers }).catch(() => ({ data: { section_visibility: {} } }))
      ]);
      
      setProfile({
        name: profileRes.data?.name || '',
        username: profileRes.data?.username || '',
        bio: profileRes.data?.bio || '',
        profile_image: profileRes.data?.profile_image || ''
      });
      
      if (visibilityRes.data?.section_visibility) {
        setVisibility(prev => ({ ...prev, ...visibilityRes.data.section_visibility }));
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/api/user/profile`, {
        name: profile.name,
        bio: profile.bio
      }, { headers });
      
      toast.success('Profile saved');
      setUser(prev => ({ ...prev, name: profile.name, bio: profile.bio }));
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const updateVisibility = async (section, isPrivate) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/user/section-visibility`, {
        section,
        is_private: isPrivate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVisibility(prev => ({ ...prev, [section]: isPrivate }));
      toast.success(`${section} visibility updated`);
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  const sections = [
    { key: 'feed', label: 'Feed', icon: MessageSquare, description: 'Your local feed posts' },
    { key: 'commitments', label: 'Commitments', icon: Check, description: 'Events you\'re attending' },
    { key: 'groups', label: 'Groups', icon: Users, description: 'Groups you\'ve joined' },
    { key: 'commons', label: 'Commons', icon: Layers, description: 'Stewarded communities' },
    { key: 'scuta', label: 'SCUTA', icon: Clock, description: 'Timeline awareness' },
    { key: 'photos', label: 'Photos', icon: Image, description: 'Your photo gallery' },
    { key: 'expression', label: 'Expression Space', icon: MessageSquare, description: 'Your personal expression' },
    { key: 'contacts', label: 'Contacts', icon: Users, description: 'People you know (always private)' },
    { key: 'circles', label: 'Circles', icon: Layers, description: 'Contact categories (always private)' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/settings')} className="p-2 -ml-2 rounded-lg hover:opacity-70">
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
              </button>
              <div className="flex items-center gap-1">
                <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Edit Backpack</h1>
                <InfoTooltip topic="myworld" />
              </div>
            </div>
            <Button onClick={saveProfile} disabled={saving} style={{ backgroundColor: 'var(--lk-sage)' }}>
              {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save</>}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Base Camp Info - NOT "Profile" */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Backpack Info</h3>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
              {profile.profile_image ? (
                <img src={profile.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => toast.info('Photo upload coming soon')}>
              <Camera className="w-4 h-4 mr-2" /> Change Photo
            </Button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Display Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                className="w-full mt-1 p-2 rounded-lg"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Username</label>
              <input
                type="text"
                value={`@${profile.username}`}
                disabled
                className="w-full mt-1 p-2 rounded-lg opacity-60"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)', border: '1px solid var(--fy-border-subtle)' }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>Username cannot be changed</p>
            </div>
            
            <div>
              <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Bio</label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                rows={3}
                placeholder="Tell people about yourself..."
                className="w-full mt-1 p-2 rounded-lg resize-none"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              />
            </div>
          </div>
        </Card>

        {/* Section Visibility */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
          <div className="flex items-center gap-1 mb-2">
            <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Section Visibility</h3>
            <InfoTooltip topic="visibility" />
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Choose who can see each section of your Backpack.
          </p>
          
          <div className="space-y-3">
            {sections.map(section => (
              <div key={section.key} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <div className="flex items-center gap-3">
                  <section.icon className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{section.label}</p>
                    <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>{section.description}</p>
                  </div>
                </div>
                
                {section.key === 'contacts' || section.key === 'circles' ? (
                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--fy-background-primary)', color: 'var(--fy-text-disabled)' }}>
                    Always Private
                  </span>
                ) : (
                  <select
                    value={visibility[section.key] ? 'private' : 'contacts'}
                    onChange={(e) => updateVisibility(section.key, e.target.value === 'private')}
                    className="text-sm px-3 py-1 rounded-lg"
                    style={{ backgroundColor: 'var(--fy-background-primary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                  >
                    <option value="private">Private</option>
                    <option value="contacts">Contacts</option>
                  </select>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Quick Links */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Quick Actions</h3>
          
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/settings/photos')}>
              <Image className="w-4 h-4 mr-2" /> Manage Photos & Featured
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/settings/categories')}>
              <Layers className="w-4 h-4 mr-2" /> Primary Categories
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/settings/location')}>
              <ArrowLeft className="w-4 h-4 mr-2 rotate-180" /> Location Settings
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/backpack')}>
              <ArrowLeft className="w-4 h-4 mr-2 rotate-180" /> View Backpack
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
